import { formatText, useTexts, type Label, type Locale } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState, ServiceState } from "../ui/pieces";
import { connectionTone, plural, routeName, usableOf, when } from "./ai";
import { useTitle } from "./parts";
import {
  Counter,
  FilterRow,
  Mark,
  MoreFilters,
  NoResults,
  Pill,
  PillDivider,
  RowsSkeleton,
  SearchBox,
  useFilters,
} from "./widgets";
import styles from "./ai.module.css";

export type ProviderItem = {
  providerId: number;
  code: string;
  name: string;
  state: string;
  group: "connected" | "pending" | "inactive";
  /** «Todos» y «Usables» de la regla única de AI Management (los de su ficha y su pestaña Modelos). */
  models: number;
  usableModels: number;
  canConnect: boolean;
  custom: boolean;
  text: boolean;
  embedding: boolean;
  vision: boolean;
  lastValidatedAt: string | null;
  routes: { routeCode: string; primary: boolean }[];
};

type Counts = { all: number; connected: number; pending: number; inactive: number };

/** «Texto y Embeddings»: una lista corta con «y» antes de la última. */
export const joinAnd = (L: Label, items: string[]) =>
  items.length > 1 ? `${items.slice(0, -1).join(", ")} ${L("ADM_AI_AND")} ${items.at(-1)}` : (items[0] ?? "");

/** Línea 2 de un proveedor: modelos y dónde se usa (por defecto o de respaldo), o lo que le falta. */
export function providerLine(L: Label, locale: Locale, item: ProviderItem): string {
  const models = usableOf(L, locale, item.usableModels, item.models);
  if (item.state === "NEEDS_KEY") return L("ADM_AI_NO_MODELS_UNTIL_KEY");
  if (item.custom) return `${L("ADM_AI_CUSTOM")} · ${models}`;
  const primary = item.routes.filter((route) => route.primary).map((route) => routeName(L, route.routeCode));
  const backup = item.routes.filter((route) => !route.primary).map((route) => routeName(L, route.routeCode));
  const parts = [models];
  if (primary.length) parts.push(formatText(L("ADM_AI_DEFAULT_IN"), joinAnd(L, primary)));
  if (backup.length) parts.push(formatText(L("ADM_AI_BACKUP_IN"), joinAnd(L, backup)));
  if (item.state === "NOT_CONNECTED") {
    const caps = [
      item.text ? L("ADM_ROUTE_TEXT") : null,
      item.embedding ? L("ADM_ROUTE_EMBEDDING") : null,
      item.vision ? L("ADM_ROUTE_VISION") : null,
    ].filter(Boolean) as string[];
    if (caps.length) parts.push(joinAnd(L, caps));
  }
  return parts.join(" · ");
}

const groups = ["", "connected", "pending", "inactive"] as const;

/**
 * IA › Proveedores (§5.9 `ia-proveedores`): buscador, píldoras por estado con su conteo sobre el total, «Más filtros»
 * con Capacidad y la lista C17 paginada en el servidor (B3). Las API keys viven en la ficha del proveedor: un proveedor
 * no se «conecta» (decisión del dueño, 2026-10-30); «Agregar API key» abre su ficha con la ventana. Lo que sí se crea
 * aquí es un proveedor personalizado.
 */
export function ProvidersPage() {
  const { L, locale } = useTexts();
  const { get, set } = useFilters();
  useTitle(L("ADM_AI_PROVIDERS"));
  const state = get("state");
  const capability = get("capability");
  const search = get("search");
  const query = new URLSearchParams(
    Object.entries({ state, capability, search }).filter(([, value]) => value) as [string, string][],
  ).toString();
  const list = useServerList<ProviderItem>(`/api/admin/ai/providers${query ? `?${query}` : ""}`);
  // Los conteos de las píldoras llegan con cada página y son sobre todos los proveedores (no sobre lo filtrado).
  const counts = (list.extra?.counts as Counts | undefined) ?? null;
  const head = (
    <PageHead title={L("ADM_AI_PROVIDERS")} sub={L("ADM_AI_PROVIDERS_SUB")}>
      <a className={`tm-btn secondary ${styles.desktopInline}`} href="/Ai/Avanzado/Personalizado">
        <ChatIcon name="plus" />
        {L("ADM_AI_CUSTOM_PROVIDER")}
      </a>
      <a
        className={styles.fab}
        aria-label={L("ADM_AI_CUSTOM_PROVIDER")}
        title={L("ADM_AI_CUSTOM_PROVIDER")}
        href="/Ai/Avanzado/Personalizado"
      >
        <ChatIcon name="plus" />
      </a>
    </PageHead>
  );
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;

  const capabilityLabel: Record<string, string> = {
    TEXT: L("ADM_ROUTE_TEXT"),
    EMBEDDING: L("ADM_ROUTE_EMBEDDING"),
    IMAGE_INPUT: L("ADM_ROUTE_VISION"),
  };
  const groupLabel = (value: string) =>
    ({ "": "ADM_AI_ALL", connected: "ADM_AI_CONNECTED", pending: "ADM_AI_PENDING", inactive: "ADM_AI_INACTIVE" })[
      value
    ]!;
  const countOf = (value: string, data: Counts) =>
    value === "connected"
      ? data.connected
      : value === "pending"
        ? data.pending
        : value === "inactive"
          ? data.inactive
          : data.all;
  const filtered = !!(state || capability || search);

  return (
    <>
      {head}
      <div className={styles.toolbar}>
        <SearchBox
          id="providers-search"
          value={search}
          placeholder={L("ADM_AI_SEARCH_PROVIDER")}
          onChange={(value) => set({ search: value })}
        />
        <FilterRow label={L("ADM_AI_FILTERS")}>
          {groups.map((value) => (
            <Pill key={value || "all"} pressed={state === value} onClick={() => set({ state: value })}>
              {L(groupLabel(value))}
              {counts ? ` ${countOf(value, counts).toLocaleString(locale)}` : null}
            </Pill>
          ))}
          {capability ? (
            <Pill
              pressed
              onClick={() => set({ capability: "" })}
              label={`${L("ADM_AI_REMOVE_FILTER")}: ${capabilityLabel[capability]}`}
            >
              {capabilityLabel[capability]}
              <ChatIcon name="x" />
            </Pill>
          ) : null}
          <PillDivider />
          <MoreFilters
            groups={[
              {
                key: "capability",
                title: L("ADM_AI_CAPABILITY"),
                options: Object.entries(capabilityLabel).map(([value, label]) => ({ value, label })),
              },
            ]}
            value={{ capability }}
            onApply={(next) => set({ capability: next.capability ?? "" })}
          />
        </FilterRow>
      </div>
      {list.state === "loading" ? (
        <RowsSkeleton rows={5} />
      ) : list.state === "error" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        filtered ? (
          <NoResults
            query={search}
            things={L("ADM_AI_PROVIDERS_THINGS")}
            onClear={() => set(search ? { search: "" } : { state: "", capability: "" })}
          />
        ) : (
          <div className="tm-empty" role="status">
            <strong>{L("ADM_AI_NO_PROVIDERS")}</strong>
            <span>{L("ADM_AI_NO_PROVIDERS_LINE")}</span>
            <a className="tm-btn secondary" href="/Ai/Avanzado/Personalizado">
              {L("ADM_AI_CUSTOM_PROVIDER")}
            </a>
          </div>
        )
      ) : (
        <>
          {filtered ? (
            <Counter>{plural(L, locale, list.total, "ADM_N_PROVIDERS_ONE", "ADM_N_PROVIDERS")}</Counter>
          ) : null}
          <ul className={styles.list} aria-label={L("ADM_AI_PROVIDERS")}>
            {list.items.map((item) => (
              <ProviderRow key={item.code} item={item} />
            ))}
          </ul>
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}

function ProviderRow({ item }: { item: ProviderItem }) {
  const { L, locale } = useTexts();
  const tone = connectionTone(L, item.state);
  const href = `/Ai/Providers/${encodeURIComponent(item.code)}`;
  const offer = item.state === "NOT_CONNECTED" && item.canConnect;
  return (
    <li className={styles.row}>
      <a className={styles.rowLinkArea} href={href}>
        <Mark name={item.name} />
        <span className={styles.rowText}>
          <span className={styles.line1}>
            <strong>{item.name}</strong>
            <ServiceState tone={tone.tone}>{tone.text}</ServiceState>
          </span>
          <span className={styles.line2}>{providerLine(L, locale, item)}</span>
        </span>
        {offer ? null : (
          <span className={styles.rowEnd}>
            <span className={styles.validated}>
              {item.lastValidatedAt
                ? formatText(L("ADM_AI_VALIDATED_AT"), when(L, locale, item.lastValidatedAt, true))
                : L("ADM_AI_NEVER_VALIDATED")}
            </span>
            <ChatIcon name="chevron-right" />
          </span>
        )}
      </a>
      {offer ? (
        <span className={styles.rowEnd}>
          <a className="tm-btn secondary" href={`${href}?conectar=1`}>
            {L("ADM_ADD_KEY")}
          </a>
        </span>
      ) : null}
    </li>
  );
}
